/**
 * Version configuration and utilities for Strive App
 */

// Import version from package.json (will be injected by build process)
import packageJson from '../../package.json'

export const APP_VERSION = packageJson.version

export interface VersionInfo {
  version: string
  major: number
  minor: number
  patch: number
  preRelease?: string | undefined
  buildTime: string
}

/**
 * Parse semantic version string into components
 */
export function parseVersion(version: string): Omit<VersionInfo, 'buildTime'> {
  const versionRegex = /^(\d+)\.(\d+)\.(\d+)(?:-(.+))?$/
  const match = version.match(versionRegex)

  if (!match) {
    throw new Error(`Invalid version format: ${version}`)
  }

  const [, major, minor, patch, preRelease] = match

  if (!major || !minor || !patch) {
    throw new Error(`Invalid version components: ${version}`)
  }

  return {
    version,
    major: parseInt(major, 10),
    minor: parseInt(minor, 10),
    patch: parseInt(patch, 10),
    preRelease: preRelease || undefined,
  }
}

/**
 * Get complete version information
 */
export function getVersionInfo(): VersionInfo {
  const parsed = parseVersion(APP_VERSION)

  return {
    ...parsed,
    buildTime:
      ((globalThis as Record<string, unknown>).__BUILD_TIME__ as string) ||
      new Date().toISOString(),
  }
}

/**
 * Compare two version strings
 * Returns: -1 if v1 < v2, 0 if v1 === v2, 1 if v1 > v2
 */
export function compareVersions(v1: string, v2: string): -1 | 0 | 1 {
  const parsed1 = parseVersion(v1)
  const parsed2 = parseVersion(v2)

  if (parsed1.major !== parsed2.major) {
    return parsed1.major < parsed2.major ? -1 : 1
  }

  if (parsed1.minor !== parsed2.minor) {
    return parsed1.minor < parsed2.minor ? -1 : 1
  }

  if (parsed1.patch !== parsed2.patch) {
    return parsed1.patch < parsed2.patch ? -1 : 1
  }

  return 0
}

/**
 * Check if version is a pre-release
 */
export function isPreRelease(version: string): boolean {
  const parsed = parseVersion(version)
  return Boolean(parsed.preRelease)
}

/**
 * Format version for display
 */
export function formatVersionDisplay(version: string): string {
  const parsed = parseVersion(version)

  if (parsed.preRelease) {
    return `v${version}`
  }

  return `v${version}`
}

/**
 * Version milestones and release notes
 */
export const VERSION_MILESTONES = {
  '0.1.0': 'MVP Foundation - Project setup, testing, and basic infrastructure',
  '0.2.0': 'Core Features - Authentication and data entry system',
  '0.3.0': 'Data Visualization - Charts, tables, and analytics dashboard',
  '0.4.0': 'PWA Features - Offline support and native app capabilities',
  '1.0.0': 'Production Release - Complete athletics performance tracking platform',
} as const

/**
 * Get milestone description for current or specific version
 */
export function getVersionMilestone(version?: string): string | undefined {
  const targetVersion = version || APP_VERSION
  return VERSION_MILESTONES[targetVersion as keyof typeof VERSION_MILESTONES]
}

/**
 * Development phase based on version
 */
export type DevelopmentPhase = 'alpha' | 'beta' | 'stable'

export function getDevelopmentPhase(version?: string): DevelopmentPhase {
  const targetVersion = version || APP_VERSION
  const parsed = parseVersion(targetVersion)

  if (parsed.major === 0) {
    if (parsed.minor < 3) return 'alpha'
    return 'beta'
  }

  return 'stable'
}
